import { Modal } from './accessibility.jsx';
import { confirmAction } from './feedback.jsx';
import { OfflineAuthorityDialog } from './offline-authority.jsx';
// app/static/js/soc/users-ui.jsx
// The Users card on the settings page and the Change-password dialog.
// Spec 2026-09-07 §5.2. Both talk to the adapter only; the server owns
// every rule, this file only names the outcomes in two languages.
import { accountSlots, bindLinkView } from './telegram-account.mjs';
import { maySetCeiling, mayResume, ownLimitText, parseAmount, seatChoices, showsSeatColumns, teamSections } from './team-logic.mjs';
import { nextFireText, taskChip } from './chat-tasks-logic.mjs';
const { useEffect, useRef, useState } = React;

const T = {
  en: {
    users: 'Users', admins: 'Admins', note: 'Named people who log in with the company slug, a username and their own password. Deactivate rather than delete: a deleted user keeps only its audit rows.',
    tenant: 'Company', slug: 'Login slug', save: 'Save', saved: 'saved', create: 'Create user', createAdmin: 'Create admin',
    username: 'Username', display: 'Display name', email: 'Email (optional)', status: 'Status', lastLogin: 'Last login',
    reset: 'Reset password', unlock: 'Unlock', deactivate: 'Deactivate', activate: 'Activate', del: 'Delete',
    confirmDel: 'Delete this user? Prefer Deactivate — a deleted user cannot be restored.',
    tempNote: 'Temporary password, valid 24 hours. The user must replace it at first login.',
    st: { active: 'active', inactive: 'inactive', locked: 'locked', must_change: 'must change', temp_expired: 'temp expired' },
    pw: { title: 'Change password', current: 'Current password', next: 'New password', again: 'Repeat it', rule: 'At least 12 characters. Not a common password, not your username, not the current one.', change: 'Change', cancel: 'Cancel', done: 'Password changed.', differ: 'The two passwords differ.', invalid: 'Invalid credentials.' },
    problem: { too_short: 'At least 12 characters.', too_long: 'At most 128 characters.', common: 'That password is too common.', contains_username: 'The password must not contain your username.', same_as_current: 'Choose a password different from the current one.', bad_username: 'Username: letters, digits, dot, dash or underscore, up to 64.', bad_slug: 'Slug: lower-case letters, digits and dashes, 2–32 characters.' },
    seat: 'Agent seat', tenantAdmin: 'Company admin', seats: { none: 'none', business: 'business', developer: 'developer' },
    seatCount: (l) => `${l.seats_held} of ${l.max_seats} seats held (${l.dev_seats_held} of ${l.max_dev_seats} developer).`,
    team: 'My agent and team', noTeam: 'Nothing here for this login: it holds no agent seat and is not a company admin.',
    company: 'This company\'s agent seats', maxSeats: 'Seats', maxDev: 'Of which developer seats',
    internet: 'Workbench Internet access',
    internetNote: 'Applies to all company environments. The agent can send data it can read to public websites. Turning access off blocks new turns and searches; active turns keep access until their deadline.',
    waitNote: 'Tasks you give the agent run in your own workspace.',
    tasks: 'This company\'s tasks', tasksNote: 'Every task of this company, whoever owns it. You can pause each one, and resume your own people\'s; a task an installation admin owns is resumed by an installation admin.',
    owner: 'Owner', next: 'Next', pause: 'Pause', resume: 'Resume', noTasks: 'No tasks yet.',
    limits: 'Agent limits', limitsNote: 'In a seat holder\'s task, a write job waits for a person\'s approval when its form moves money (this company\'s list), when the task\'s mandate does not name its interface, or when its amount is above the owner\'s threshold. The company admin sets each person\'s ceiling; each person sets a threshold up to it.',
    myThreshold: 'My threshold', none: 'none', notify: 'Notices', notifyOpt: { all: 'all notices', critical: 'only what needs me' },
    notifyNote: '"Only what needs me" skips the mail sent when a run finishes; a question waiting for your answer is always sent.',
    notANumber: 'Type a number, or leave it empty for none.',
    ceilings: 'Ceilings', ceiling: 'Ceiling', threshold: 'Threshold', noHolders: 'No one in this company holds a seat.',
    ownCeiling: 'Your own ceiling is set by an installation admin.',
    ceilingsNote: 'The most each seat holder\'s own threshold may be. Empty clears it, and then every amount asks. Lowering it below a threshold lowers the threshold with it.',
    moneyForms: 'Money-moving forms', moneyNote: 'A write job on a form on this list always asks, whatever the mandate and the threshold say, at every level of its interface. Add a form by its name in this company\'s dictionary.',
    formName: 'Form name (ENAME)', add: 'Add', remove: 'Remove', noForms: 'The list is empty.',
    confirmRemove: (e) => `Take ${e} off the list? A write job on it then asks only when its mandate or the threshold says so.`,
  },
  he: {
    users: 'משתמשים', admins: 'מנהלים', note: 'אנשים עם שם — נכנסים עם מזהה החברה, שם משתמש וסיסמה אישית. עדיף להשבית מאשר למחוק: משתמש שנמחק משאיר רק את רשומות הביקורת שלו.',
    tenant: 'חברה', slug: 'מזהה כניסה', save: 'שמור', saved: 'נשמר', create: 'משתמש חדש', createAdmin: 'מנהל חדש',
    username: 'שם משתמש', display: 'שם לתצוגה', email: 'אימייל (לא חובה)', status: 'מצב', lastLogin: 'כניסה אחרונה',
    reset: 'איפוס סיסמה', unlock: 'שחרור נעילה', deactivate: 'השבתה', activate: 'הפעלה', del: 'מחיקה',
    confirmDel: 'למחוק את המשתמש? עדיף להשבית — משתמש שנמחק אינו ניתן לשחזור.',
    tempNote: 'סיסמה זמנית, תקפה 24 שעות. המשתמש חייב להחליף אותה בכניסה הראשונה.',
    st: { active: 'פעיל', inactive: 'מושבת', locked: 'נעול', must_change: 'חייב להחליף', temp_expired: 'הזמנית פגה' },
    pw: { title: 'שינוי סיסמה', current: 'סיסמה נוכחית', next: 'סיסמה חדשה', again: 'שוב', rule: 'לפחות 12 תווים. לא סיסמה נפוצה, לא שם המשתמש, ולא הסיסמה הנוכחית.', change: 'שנה', cancel: 'ביטול', done: 'הסיסמה שונתה.', differ: 'הסיסמאות אינן זהות.', invalid: 'פרטי הכניסה שגויים.' },
    problem: { too_short: 'לפחות 12 תווים.', too_long: 'לכל היותר 128 תווים.', common: 'הסיסמה נפוצה מדי.', contains_username: 'הסיסמה לא יכולה להכיל את שם המשתמש.', same_as_current: 'בחרו סיסמה שונה מהנוכחית.', bad_username: 'שם משתמש: אותיות, ספרות, נקודה, מקף או קו תחתון, עד 64.', bad_slug: 'מזהה: אותיות קטנות, ספרות ומקפים, 2–32 תווים.' },
    seat: 'מושב סוכן', tenantAdmin: 'מנהל החברה', seats: { none: 'ללא', business: 'עסקי', developer: 'מפתח' },
    seatCount: (l) => `${l.seats_held} מתוך ${l.max_seats} מושבים בשימוש (${l.dev_seats_held} מתוך ${l.max_dev_seats} מושבי מפתח).`,
    team: 'הסוכן והצוות שלי', noTeam: 'אין כאן דבר לכניסה הזו: אין לה מושב סוכן והיא אינה מנהלת החברה.',
    company: 'מושבי הסוכן של החברה', maxSeats: 'מושבים', maxDev: 'מהם מושבי מפתח',
    internet: 'גישה לאינטרנט בסביבת העבודה',
    internetNote: 'חל על כל סביבות החברה. הסוכן יכול לשלוח לאתרים ציבוריים מידע שהוא מורשה לקרוא. כיבוי הגישה חוסם תורות חדשים וחיפושים; תור פעיל שומר על הגישה עד מגבלת הזמן שלו.',
    waitNote: 'משימות שנותנים לסוכן רצות במרחב העבודה האישי שלך.',
    tasks: 'המשימות של החברה', tasksNote: 'כל משימה של החברה, של כל בעלים. אפשר להשהות כל אחת, ולהמשיך את אלה של אנשי החברה; משימה של מנהל התקנה ממשיך מנהל התקנה.',
    owner: 'בעלים', next: 'הבא', pause: 'השהיה', resume: 'המשך', noTasks: 'אין משימות עדיין.',
    limits: 'מגבלות הסוכן', limitsNote: 'במשימה של בעל מושב, עבודת כתיבה ממתינה לאישור של אדם כשהטופס שלה מזיז כסף (הרשימה של החברה), כשההרשאה של המשימה לא מציינת את הממשק שלה, או כשהסכום שלה מעל הסף של הבעלים. מנהל החברה קובע לכל אדם תקרה; כל אדם קובע סף עד אליה.',
    myThreshold: 'הסף שלי', none: 'ללא', notify: 'הודעות', notifyOpt: { all: 'כל ההודעות', critical: 'רק מה שדורש אותי' },
    notifyNote: '"רק מה שדורש אותי" מדלג על הדואר שנשלח בסיום ריצה; שאלה שממתינה לתשובתך נשלחת תמיד.',
    notANumber: 'הקלידו מספר, או השאירו ריק ללא ערך.',
    ceilings: 'תקרות', ceiling: 'תקרה', threshold: 'סף', noHolders: 'לאף אחד בחברה אין מושב.',
    ownCeiling: 'את התקרה שלך קובע מנהל התקנה.',
    ceilingsNote: 'הערך המרבי של הסף שכל בעל מושב קובע לעצמו. ריק מבטל את התקרה, ואז הסוכן שואל על כל סכום. הורדה מתחת לסף מורידה גם את הסף.',
    moneyForms: 'טפסים שמזיזים כסף', moneyNote: 'עבודת כתיבה על טופס שברשימה תמיד שואלת, מה שלא יגידו ההרשאה והסף, בכל רמה של הממשק שלה. מוסיפים טופס לפי שמו במילון של החברה.',
    formName: 'שם טופס (ENAME)', add: 'הוספה', remove: 'הסרה', noForms: 'הרשימה ריקה.',
    confirmRemove: (e) => `להסיר את ${e} מהרשימה? עבודת כתיבה עליו תשאל אז רק כשההרשאה או הסף מחייבים.`,
  },
};
const STATUS_CLASS = { active: 'st-ok', inactive: 'st-neutral', locked: 'st-danger', must_change: 'st-warn', temp_expired: 'st-danger' };

// The server answers {detail: {code}} for a rule and {detail: "text"} for
// the rest; the adapter puts either on err.detail.
function problemOf(err, t) {
  const d = err && err.detail;
  // Agent-seat refusals (422) carry {code, message}: the message says the numbers.
  if (d && typeof d === 'object' && d.code) return t.problem[d.code] || d.message || d.code;
  return (typeof d === 'string' && d) || String(err && err.message || err);
}

// `seats` is null, or {limits, selfId}: the agent-seat column, shown only while agent
// seats are on and only for a company's users. `admins` is null, or {selfId}: the
// company-admin column, shown while the server puts `tenant_admin` on the rows (the
// role or seats on for that company). `selfId` is the signed-in company admin, whose
// own role the server will not change.
function UsersTable({ t, lang, users, onAction, seats, admins, namedAdmin }) {
  if (users === null) return <div className="set-status mono">…</div>;
  return (
    <div className="tbl-wrap" tabIndex={0}>
      <table className="tbl">
        <thead><tr>
          <th>{t.username}</th><th>{t.display}</th><th>{t.email}</th><th>{t.status}</th><th>{t.lastLogin}</th>
          {seats && <th>{t.seat}</th>}{admins && <th>{t.tenantAdmin}</th>}<th></th>
        </tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td className="mono" dir="ltr">{u.username}</td>
              <td dir="auto">{u.display_name}</td>
              <td className="mono" dir="ltr">{u.email || '—'}</td>
              <td><span className={'chip ' + (STATUS_CLASS[u.status] || 'st-neutral')}>{t.st[u.status] || u.status}</span></td>
              <td className="mono">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
              {seats && <td>
                <select className="set-input" aria-label={`${t.seat} ${u.username}`} value={u.agent_seat}
                        onChange={e => onAction('seat', u, e.target.value)}>
                  {seatChoices(seats.limits, u).map(s => <option key={s} value={s}>{t.seats[s]}</option>)}
                </select>
              </td>}
              {admins && <td>
                <input type="checkbox" aria-label={`${t.tenantAdmin} ${u.username}`} checked={!!u.tenant_admin}
                       disabled={u.id === admins.selfId} onChange={e => onAction('tadmin', u, e.target.checked)}/>
              </td>}
              <td>
                <div className="set-row">
                  {namedAdmin && u.offline_authority && <button type="button" className="btn btn-sm" onClick={() => onAction('offline', u)}>{lang === 'he' ? 'הרשאות אופליין' : 'Offline permissions'}</button>}
                  <button type="button" className="btn btn-sm" onClick={() => onAction('reset', u)}>{t.reset}</button>
                  {u.locked && <button type="button" className="btn btn-sm" onClick={() => onAction('unlock', u)}>{t.unlock}</button>}
                  <button type="button" className="btn btn-sm" onClick={() => onAction(u.is_active ? 'deactivate' : 'activate', u)}>{u.is_active ? t.deactivate : t.activate}</button>
                  <button type="button" className="btn btn-sm danger" onClick={() => onAction('delete', u)}>{t.del}</button>
                </div>
              </td>
            </tr>
          ))}
          {users.length === 0 && <tr><td colSpan={6 + (seats ? 1 : 0) + (admins ? 1 : 0)} className="set-status">—</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// One card, three modes: tenantId a number -> that tenant's users and its
// slug; tenantId null -> the installation admins; `own` -> a company admin's
// own company (no company switch, no slug, never an admin). `seatsOn` adds
// the agent-seat columns to a company's users (agent seats spec §6).
function UsersCard({ lang, tenants, tenantId, onTenant, own = false, seatsOn = false, selfId = null, namedAdmin = false }) {
  const t = T[lang === 'he' ? 'he' : 'en'];
  const adminMode = tenantId === null;
  const [users, setUsers] = useState(null);
  const [offlineUser, setOfflineUser] = useState(null);
  const userRead = useRef(0);
  const [error, setError] = useState('');
  const [minted, setMinted] = useState(null);           // { username, temporary_password }
  const [form, setForm] = useState({ username: '', display_name: '', email: '' });
  const [slug, setSlug] = useState('');
  const [slugSaved, setSlugSaved] = useState(false);
  const [limits, setLimits] = useState(null);           // the company's seat counts
  const tenant = tenants.find(x => x.id === tenantId) || null;
  const showSeats = showsSeatColumns(seatsOn, tenant, adminMode);
  const showAdmins = !adminMode && (showSeats || (users || []).some(u => u.tenant_admin !== undefined));

  const load = () => {
    const read = ++userRead.current;
    if (showSeats) PCMAdapter.getTenantAgentLimits(tenantId).then(setLimits).catch(() => setLimits(null));
    return PCMAdapter.getUsers(adminMode ? undefined : tenantId)
      .then(d => { if (read === userRead.current) setUsers(d.users); })
      .catch(e => { if (read === userRead.current) { setUsers([]); setError(problemOf(e, t)); } });
  };
  useEffect(() => { setUsers(null); setMinted(null); setError(''); setSlugSaved(false); load(); return () => { userRead.current++; }; }, [tenantId, seatsOn, showSeats]);
  // Only the slug VALUE follows the tenant list; the saved marker must survive
  // the reload that a successful save itself triggers (it clears on typing).
  useEffect(() => { setSlug(tenant ? (tenant.slug || '') : ''); }, [tenantId, tenants]);

  const act = async (kind, u, value) => {
    if (kind === 'offline') { setOfflineUser(u); return; }
    if (kind === 'delete' && !await confirmAction(t.confirmDel, { lang, danger: true, confirmLabel: t.del })) return;
    setError(''); setMinted(null);
    const p = kind === 'reset' ? PCMAdapter.resetUserPassword(u.id).then(d => setMinted({ username: u.username, temporary_password: d.temporary_password, kind: 'reset' }))
      : kind === 'unlock' ? PCMAdapter.unlockUser(u.id)
      : kind === 'deactivate' ? PCMAdapter.patchUser(u.id, { is_active: false })
      : kind === 'activate' ? PCMAdapter.patchUser(u.id, { is_active: true })
      : kind === 'seat' ? PCMAdapter.patchUser(u.id, { agent_seat: value })
      : kind === 'tadmin' ? PCMAdapter.patchUser(u.id, { tenant_admin: value })
      : kind === 'delete' ? PCMAdapter.deleteUser(u.id)
      : Promise.resolve();
    p.then(load).catch(e => setError(problemOf(e, t)));
  };
  const create = () => {
    setError(''); setMinted(null);
    PCMAdapter.createUser({ tenant_id: adminMode ? null : tenantId, username: form.username.trim(),
                            display_name: form.display_name.trim(), email: form.email.trim() || null })
      .then(d => { setMinted({ username: d.username, temporary_password: d.temporary_password, kind: 'create' }); setForm({ username: '', display_name: '', email: '' }); load(); })
      .catch(e => setError(problemOf(e, t)));
  };
  const saveSlug = () => {
    setError(''); setSlugSaved(false);
    PCMAdapter.putTenantSlug(tenantId, slug.trim().toLowerCase())
      .then(() => { setSlugSaved(true); onTenant && onTenant(); })
      .catch(e => setError(problemOf(e, t)));
  };

  return (
    <div className="card set-section wide">
      <h3>{adminMode ? t.admins : t.users}</h3>
      <p className="set-note" dir="auto">{t.note}</p>
      {!adminMode && !own && (
        <div className="set-row">
          <span className="set-field-label">{t.tenant}</span>
          <select className="set-input" aria-label={t.tenant} value={tenantId} onChange={e => onTenant(Number(e.target.value))}>
            {tenants.map(x => <option key={x.id} value={x.id}>{x.company_name}</option>)}
          </select>
          <span className="set-field-label">{t.slug}</span>
          <input className="set-input mono" dir="ltr" aria-label={t.slug} value={slug} onChange={e => { setSlug(e.target.value); setSlugSaved(false); }}/>
          <button type="button" className="btn btn-sm" disabled={!slug.trim() || slug.trim() === (tenant && tenant.slug)} onClick={saveSlug}>{t.save}</button>
          <span className="set-status mono" role="status">{slugSaved ? t.saved : ''}</span>
        </div>
      )}
      {showSeats && limits && <p className="set-note" dir="auto">{t.seatCount(limits)}</p>}
      <UsersTable t={t} lang={lang} users={users} onAction={act}
                  namedAdmin={namedAdmin} seats={showSeats ? { limits, selfId } : null} admins={showAdmins ? { selfId } : null}/>
      {offlineUser && <OfflineAuthorityDialog lang={lang} user={offlineUser} tenant={tenant} onClose={() => setOfflineUser(null)}
        reloadUser={async () => {
          const read = ++userRead.current;
          const data = await PCMAdapter.getUsers(tenantId);
          const fresh = data.users.find(row => row.id === offlineUser.id);
          if (!fresh) throw new Error(lang === 'he' ? 'המשתמש אינו זמין עוד.' : 'This user is no longer available.');
          if (read === userRead.current) setUsers(data.users);
          return fresh;
        }}/>}
      <div className="usr-form">
        <label className="perm-field"><span className="set-field-label">{t.username}</span>
          <input className="set-input" dir="ltr" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}/>
        </label>
        <label className="perm-field"><span className="set-field-label">{t.display}</span>
          <input className="set-input" dir="auto" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}/>
        </label>
        <label className="perm-field"><span className="set-field-label">{t.email}</span>
          <input className="set-input" dir="ltr" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/>
        </label>
        <button type="button" className="btn btn-sm primary" disabled={!form.username.trim()} onClick={create}>{adminMode ? t.createAdmin : t.create}</button>
      </div>
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
      {minted && window.OnceBox && (
        <window.OnceBox label={`${minted.username} · ${minted.kind === 'reset' ? t.reset : (adminMode ? t.createAdmin : t.create)}`} value={minted.temporary_password} lang={lang} note={t.tempNote}/>
      )}
    </div>
  );
}

function OfflineDeveloperCreateCard({ lang, slug, onCreated, onDenied }) {
  const he = lang === 'he', t = T[he ? 'he' : 'en'];
  const [minted, setMinted] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const active = useRef(true);
  useEffect(() => {
    const clear = () => { active.current = false; setMinted(null); };
    window.addEventListener('pcm-session-closing', clear);
    return () => { active.current = false; window.removeEventListener('pcm-session-closing', clear); };
  }, []);
  const create = async event => {
    event.preventDefault();
    const form = event.currentTarget, fields = new FormData(form);
    setBusy(true); setError(''); setMinted(null);
    try {
      const result = await PCMAdapter.createTeamOfflineDeveloper({
        username: fields.get('username').trim(), display_name: fields.get('display_name').trim(),
        email: fields.get('email').trim() || null,
      });
      if (!active.current) return;
      setMinted(result); form.reset(); await onCreated();
    } catch (e) {
      if (!active.current) return;
      if (e.status === 401 || e.status === 403) onDenied();
      setError(problemOf(e, t));
    } finally { if (active.current) setBusy(false); }
  };
  return <section>
    <h3>{he ? 'יצירת מפתח לא מחובר' : 'Create offline developer'}</h3>
    <p className="set-note">{he
      ? 'שם משתמש הוא שדה חובה. החשבון מקבל מושב מפתח בלבד, ללא ניהול או קבוצות נתונים. טל מקצה הרשאות נתונים.'
      : 'Username is required. The account gets a developer seat with no admin roles or data groups. Tal assigns data access.'}</p>
    <form className="usr-form" aria-label={he ? 'יצירת מפתח לא מחובר' : 'Create offline developer'} method="post" onSubmit={create}>
      <label className="perm-field" htmlFor="offline-dev-username"><span className="set-field-label">{t.username}</span>
        <input id="offline-dev-username" name="username" className="set-input" dir="ltr" required maxLength={64}
          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}/></label>
      <label className="perm-field" htmlFor="offline-dev-display"><span className="set-field-label">{t.display}</span>
        <input id="offline-dev-display" name="display_name" className="set-input" dir="auto" autoComplete="off"/></label>
      <label className="perm-field" htmlFor="offline-dev-email"><span className="set-field-label">{t.email}</span>
        <input id="offline-dev-email" name="email" type="email" className="set-input" dir="ltr" autoComplete="off"
          autoCorrect="off" autoCapitalize="off" spellCheck={false}/></label>
      <button type="submit" className="btn btn-sm primary" disabled={busy}>{he ? 'יצירת מפתח' : 'Create developer'}</button>
    </form>
    {error && <div className="set-error" role="alert" dir="auto">{error}</div>}
    {minted && <>
      <p className="set-note">{t.slug}: <span className="mono" dir="ltr">{slug}</span> · {t.username}: <span className="mono" dir="ltr">{minted.username}</span></p>
      <window.OnceBox label={minted.username} value={minted.temporary_password} lang={lang} note={t.tempNote}/>
    </>}
  </section>;
}

// A company's agent seats (agent seats spec §6). An installation admin sets
// them; a company admin sees its own read-only (`tenantId` fixed).
function TenantLimitsCard({ lang, tenants, tenantId: fixedTenant = null }) {
  const t = T[lang === 'he' ? 'he' : 'en'];
  const readOnly = fixedTenant !== null;
  const [tenantId, setTenantId] = useState(fixedTenant ?? (tenants[0] ? tenants[0].id : null));
  const [view, setView] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const adopt = (v) => { setView(v); setForm({ max_seats: String(v.max_seats), max_dev_seats: String(v.max_dev_seats) }); };
  useEffect(() => {
    if (tenantId === null) return;
    // view and form reset together (the render reads both), and a response for
    // a company no longer selected is dropped.
    let stale = false;
    setView(null); setForm(null); setError(''); setSaved(false);
    PCMAdapter.getTenantAgentLimits(tenantId)
      .then(v => { if (!stale) adopt(v); }).catch(e => { if (!stale) setError(problemOf(e, t)); });
    return () => { stale = true; };
  }, [tenantId]);
  if (tenantId === null) return null;
  const save = () => {
    setError(''); setSaved(false); setSaving(true);
    PCMAdapter.putTenantAgentLimits(tenantId, { max_seats: Number(form.max_seats), max_dev_seats: Number(form.max_dev_seats) })
      .then(v => { adopt(v); setSaved(true); }).catch(e => setError(problemOf(e, t)))
      .finally(() => setSaving(false));
  };
  const changeInternet = (enabled) => {
    setError(''); setSaved(false); setSaving(true);
    PCMAdapter.putTenantAgentInternet(tenantId, enabled)
      .then(v => { setView(v); setSaved(true); })
      .catch(e => setError(problemOf(e, t))).finally(() => setSaving(false));
  };
  return (
    <form className="card set-section wide" onSubmit={e => { e.preventDefault(); save(); }}>
      <h3>{t.company}</h3>
      {!readOnly && (
        <div className="set-row">
          <span className="set-field-label">{t.tenant}</span>
          <select className="set-input" aria-label={t.tenant} disabled={saving} value={tenantId} onChange={e => setTenantId(Number(e.target.value))}>
            {tenants.map(x => <option key={x.id} value={x.id}>{x.company_name}</option>)}
          </select>
        </div>
      )}
      {!form ? <div className="set-status mono">…</div> : <>
        <p className="set-note" dir="auto">{t.seatCount(view)}</p>
        <label className="set-field-label" htmlFor="tl-seats" dir="auto">{t.maxSeats}</label>
        <div className="set-row"><input id="tl-seats" className="set-input" type="number" min="0" step="1" dir="ltr" disabled={readOnly} value={form.max_seats} onChange={e => setForm({ ...form, max_seats: e.target.value })}/></div>
        <label className="set-field-label" htmlFor="tl-dev" dir="auto">{t.maxDev}</label>
        <div className="set-row"><input id="tl-dev" className="set-input" type="number" min="0" step="1" dir="ltr" disabled={readOnly} value={form.max_dev_seats} onChange={e => setForm({ ...form, max_dev_seats: e.target.value })}/></div>
        <label className="set-row" dir="auto">
          <input type="checkbox" checked={!!view.internet} disabled={readOnly || saving}
            aria-describedby="tl-internet-note" onChange={e => changeInternet(e.target.checked)}/>
          <span>{t.internet}</span>
        </label>
        <p id="tl-internet-note" className="set-note" dir="auto">{t.internetNote}</p>
        {!readOnly && <div className="set-row">
          <button type="submit" className="btn btn-sm primary" disabled={saving}>{t.save}</button>
          <span className="set-status mono" role="status">{saved ? t.saved : ''}</span>
        </div>}
      </>}
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
    </form>
  );
}

// The Agent limits card (agent seats PR 3; design handoff 2026-10-02 §4, spec
// §6). `own`: the viewer's own threshold and notices under its company admin's
// ceiling (GET/PUT /api/v1/auth/me/agent-policy -- a seat holder's login only;
// the PUT needs both keys, so a notices change resends the threshold).
// `company`: each seat holder's ceiling and the company's money-moving list --
// a company admin's own company (`tenantId` fixed), or an installation admin's
// pick, as TenantLimitsCard. Every write adopts the server's answer, not the
// draft: a lowered ceiling may lower that user's threshold in the same write
// (limits.set_ceiling). A 422 shows its message, which names the limit.
function AgentLimitsCard({ lang, tenants = [], tenantId: fixedTenant = null, own = false, company = false, selfId = null }) {
  const t = T[lang === 'he' ? 'he' : 'en'];
  const [tenantId, setTenantId] = useState(fixedTenant ?? (tenants[0] ? tenants[0].id : null));
  const [mine, setMine] = useState(null);               // {ceiling, threshold, notify}
  const [mineDraft, setMineDraft] = useState({ threshold: '', notify: 'all' });
  const [mineSaved, setMineSaved] = useState(false);
  const [rows, setRows] = useState(null);               // [{user, policy}] for each seat holder
  const [drafts, setDrafts] = useState({});             // user id -> the ceiling box's text
  const [forms, setForms] = useState(null);
  const [newForm, setNewForm] = useState('');
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState(null);
  const errorId = React.useId();
  const fail = (e) => { setInvalid(null); setError(problemOf(e, t)); };
  const adoptMine = (p) => { setMine(p); setMineDraft({ threshold: p.threshold == null ? '' : String(p.threshold), notify: p.notify }); };
  useEffect(() => { if (own) PCMAdapter.getMyAgentPolicy().then(adoptMine).catch(fail); }, [own]);
  useEffect(() => {
    if (!company || tenantId === null) return;
    // As TenantLimitsCard: a response for a company no longer selected is dropped.
    let stale = false;
    setRows(null); setForms(null); setDrafts({}); setError(''); setInvalid(null);
    PCMAdapter.getUsers(tenantId)
      .then(d => Promise.all(d.users.filter(u => u.agent_seat && u.agent_seat !== 'none')
        .map(u => PCMAdapter.getUserAgentPolicy(tenantId, u.id).then(policy => ({ user: u, policy })))))
      .then(r => { if (!stale) setRows(r); }).catch(e => { if (!stale) fail(e); });
    PCMAdapter.getMoneyForms(tenantId).then(d => { if (!stale) setForms(d.forms); }).catch(e => { if (!stale) fail(e); });
    return () => { stale = true; };
  }, [company, tenantId]);
  if (!own && !company) return null;

  const saveMine = () => {
    const threshold = parseAmount(mineDraft.threshold);
    if (threshold === undefined) { setInvalid('threshold'); setError(`${t.myThreshold}: ${t.notANumber}`); return; }
    setError(''); setInvalid(null); setMineSaved(false);
    PCMAdapter.putMyAgentPolicy({ threshold, notify: mineDraft.notify })
      .then(p => { adoptMine(p); setMineSaved(true); }).catch(fail);
  };
  const saveCeiling = (u) => {
    const ceiling = parseAmount(drafts[u.id]);
    if (ceiling === undefined) { setInvalid(u.id); setError(`${t.ceiling} ${u.username}: ${t.notANumber}`); return; }
    setError(''); setInvalid(null);
    PCMAdapter.putUserAgentCeiling(tenantId, u.id, ceiling).then(policy => {
      setRows(prev => prev.map(r => (r.user.id === u.id ? { ...r, policy } : r)));
      setDrafts(prev => { const { [u.id]: _, ...rest } = prev; return rest; });
    }).catch(fail);
  };
  const addForm = () => {
    setError('');
    PCMAdapter.putMoneyForm(tenantId, newForm.trim()).then(d => { setForms(d.forms); setNewForm(''); }).catch(fail);
  };
  const removeForm = async (ename) => {
    if (!await confirmAction(t.confirmRemove(ename), { lang, danger: true, confirmLabel: t.remove })) return;
    setError('');
    PCMAdapter.deleteMoneyForm(tenantId, ename).then(d => setForms(d.forms)).catch(fail);
  };
  const shown = (n) => (n == null ? '' : String(n));

  return (
    <div className="card set-section wide">
      <h3>{t.limits}</h3>
      <p className="set-note" dir="auto">{t.limitsNote}</p>
      {own && <form onSubmit={e => { e.preventDefault(); saveMine(); }}>
        <label className="set-field-label" htmlFor="al-threshold" dir="auto">{t.myThreshold}</label>
        <p className="set-note" dir="auto">{mine ? ownLimitText(mine, lang) : '…'}</p>
        <div className="set-row">
          <input id="al-threshold" className="set-input" type="text" inputMode="decimal" dir="ltr" placeholder={t.none}
                 aria-invalid={invalid === 'threshold' ? true : undefined} aria-describedby={invalid === 'threshold' ? errorId : undefined}
                 disabled={!mine} value={mineDraft.threshold}
                 onChange={e => { setMineDraft({ ...mineDraft, threshold: e.target.value }); setMineSaved(false); setInvalid(null); setError(''); }}/>
          <select className="set-input" aria-label={t.notify} disabled={!mine} value={mineDraft.notify}
                  onChange={e => { setMineDraft({ ...mineDraft, notify: e.target.value }); setMineSaved(false); }}>
            {['all', 'critical'].map(n => <option key={n} value={n}>{t.notifyOpt[n]}</option>)}
          </select>
          <button type="submit" className="btn btn-sm" disabled={!mine}>{t.save}</button>
          <span className="set-status mono" role="status">{mineSaved ? t.saved : ''}</span>
        </div>
        <p className="set-note" dir="auto">{t.notifyNote}</p>
      </form>}
      {company && fixedTenant === null && (
        <div className="set-row">
          <span className="set-field-label">{t.tenant}</span>
          <select className="set-input" aria-label={t.tenant} value={tenantId} onChange={e => setTenantId(Number(e.target.value))}>
            {tenants.map(x => <option key={x.id} value={x.id}>{x.company_name}</option>)}
          </select>
        </div>
      )}
      {company && <>
        <span className="set-field-label" dir="auto">{t.ceilings}</span>
        <p className="set-note" dir="auto">{t.ceilingsNote}</p>
        {rows === null ? <div className="set-status mono">…</div> : (
          <div className="tbl-wrap" tabIndex={0}>
            <table className="tbl">
              <thead><tr><th>{t.username}</th><th>{t.seat}</th><th>{t.ceiling}</th><th>{t.threshold}</th></tr></thead>
              <tbody>
                {rows.map(({ user: u, policy }) => (
                  <tr key={u.id}>
                    <td className="mono" dir="ltr">{u.username}</td>
                    <td>{t.seats[u.agent_seat] || u.agent_seat}</td>
                    <td>
                      {maySetCeiling(u, selfId) ? (
                      <form className="set-row" onSubmit={e => { e.preventDefault(); saveCeiling(u); }}>
                        <input className="set-input" type="text" inputMode="decimal" dir="ltr" placeholder={t.none}
                               aria-label={`${t.ceiling} ${u.username}`}
                               aria-invalid={invalid === u.id ? true : undefined} aria-describedby={invalid === u.id ? errorId : undefined}
                               value={drafts[u.id] !== undefined ? drafts[u.id] : shown(policy.ceiling)}
                               onChange={e => { setDrafts({ ...drafts, [u.id]: e.target.value }); setInvalid(null); setError(''); }}/>
                        <button type="submit" className="btn btn-sm" disabled={drafts[u.id] === undefined}>{t.save}</button>
                      </form>
                      ) : (
                      <span className="mono" title={t.ownCeiling}>{policy.ceiling == null ? '—' : policy.ceiling} <span className="set-note" dir="auto">{t.ownCeiling}</span></span>
                      )}
                    </td>
                    <td className="mono">{policy.threshold == null ? '—' : policy.threshold}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={4} className="set-status" dir="auto">{t.noHolders}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <span className="set-field-label" dir="auto">{t.moneyForms}</span>
        <p className="set-note" dir="auto">{t.moneyNote}</p>
        {forms === null ? <div className="set-status mono">…</div> : (
          <div className="tbl-wrap" tabIndex={0}>
            <table className="tbl">
              <tbody>
                {forms.map(ename => (
                  <tr key={ename}>
                    <td className="mono" dir="ltr">{ename}</td>
                    <td><button type="button" className="btn btn-sm danger" onClick={() => removeForm(ename)}>{t.remove}</button></td>
                  </tr>
                ))}
                {forms.length === 0 && <tr><td colSpan={2} className="set-status" dir="auto">{t.noForms}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <form className="set-row" onSubmit={e => { e.preventDefault(); addForm(); }}>
          <input className="set-input mono" dir="ltr" placeholder={t.formName} aria-label={t.formName}
                 value={newForm} onChange={e => setNewForm(e.target.value)}/>
          <button type="submit" className="btn btn-sm" disabled={!newForm.trim() || forms === null}>{t.add}</button>
        </form>
      </>}
      {error && <div id={errorId} role="alert" className="set-error" dir="auto">{error}</div>}
    </div>
  );
}

// Every task of a company, for its company admin (Tal, 2026-10-01): pause any
// of them, resume its own people's (Tal, 2026-10-02) -- the server refuses
// anything else. The rows are
// GET /api/v1/agent-tasks's task dicts; the owner's name comes from the
// company's user list, which this admin may read.
function CompanyTasksCard({ lang, tenantId }) {
  const t = T[lang === 'he' ? 'he' : 'en'];
  const [tasks, setTasks] = useState(null);
  const [names, setNames] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => PCMAdapter.getAgentTasks({ tenant_id: tenantId })
    .then(d => setTasks(d.tasks)).catch(e => { setTasks([]); setError(problemOf(e, t)); });
  useEffect(() => {
    load();
    // Resume needs this list (mayResume): a failure says so rather than hiding Resume silently.
    PCMAdapter.getUsers(tenantId).then(d => setNames(Object.fromEntries(d.users.map(u => [u.id, u.username])))).catch(e => setError(problemOf(e, t)));
  }, [tenantId]);
  const ids = new Set(Object.keys(names).map(Number));
  const act = (task, action) => {
    setBusy(true); setError('');
    PCMAdapter.patchAgentTask(task.id, tenantId, { action })
      .then(() => { setBusy(false); load(); }).catch(e => { setBusy(false); setError(problemOf(e, t)); });
  };
  return (
    <div className="card set-section wide">
      <h3>{t.tasks}</h3>
      <p className="set-note" dir="auto">{t.tasksNote}</p>
      {tasks === null ? <div className="set-status mono">…</div> : (
        <div className="tbl-wrap" tabIndex={0}>
          <table className="tbl">
            <thead><tr><th>#</th><th></th><th>{t.owner}</th><th>{t.status}</th><th>{t.next}</th><th></th></tr></thead>
            <tbody>
              {tasks.map(task => (
                <tr key={task.id}>
                  <td className="mono">{task.id}</td>
                  <td dir="auto">{task.title}{task.status_reason && <div className="set-note" dir="auto">{task.status_reason}</div>}</td>
                  <td className="mono" dir="ltr">{names[task.owner_user_id] || task.owner_user_id}</td>
                  <td><span className={`chip ${taskChip(task)}`}>{task.status}</span></td>
                  <td className="mono">{nextFireText(task, lang)}</td>
                  <td>
                    {task.status === 'active' && <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act(task, 'pause')}>{t.pause}</button>}
                    {mayResume(task, ids) && <button type="button" className="btn btn-sm" disabled={busy} onClick={() => act(task, 'resume')}>{t.resume}</button>}
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && <tr><td colSpan={6} className="set-status" dir="auto">{t.noTasks}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
    </div>
  );
}

function ContactCard({ lang }) {
  const he = lang === 'he';
  const [card, setCard] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const adopt = d => { setCard(d); setEmail(d.email || ''); setPhone(d.phone || ''); };
  useEffect(() => {
    let alive = true;
    PCMAdapter.getMyContact().then(d => { if (alive) adopt(d); })
      .catch(e => { if (alive) setError(problemOf(e, T[he ? 'he' : 'en'])); });
    return () => { alive = false; };
  }, []);
  const act = async kind => {
    if (busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      // Save a phone edit alone without silently issuing another email code.
      const body = {phone};
      const queue = kind === 'resend' || (email.trim() && (email.trim() !== (card.email || '') || !card.email_verified_at));
      if (queue) body.email = email.trim();
      const d = kind === 'verify' ? await PCMAdapter.verifyMyContact(code) : await PCMAdapter.putMyContact(body);
      adopt(d); setCode('');
      setMessage(kind === 'verify' ? (he ? 'כתובת הדואר אומתה.' : 'Email verified.')
        : queue ? (he ? 'הודעת אימות בתור. הקוד תקף ל-15 דקות; המסירה תלויה בהפעלת הדואר.' : 'Verification email queued. The code expires in 15 minutes; delivery depends on mail being enabled.')
        : (he ? 'נשמר' : 'Saved'));
    } catch (e) { setError(problemOf(e, T[he ? 'he' : 'en'])); }
    finally { setBusy(false); }
  };
  return <div className="card set-section wide">
    <h3>{he ? 'פרטי הקשר שלי' : 'My verified contact'}</h3>
    <p className="set-note">{he ? 'הזמנות בדואר דורשות כתובת מאומתת. כעת מאשרים בסביבת העבודה.' : 'Orders by mail require a verified address. Approvals currently happen in the workbench.'}</p>
    <p className="set-status" role="status">{!card ? '…' : card.email_verified_at ? (he ? 'מאומת' : 'Verified') : (he ? 'לא מאומת' : 'Not verified')}{card?.email_pending ? (he ? ' · קוד ממתין' : ' · code pending') : ''}</p>
    <form onSubmit={e => { e.preventDefault(); act('save'); }}>
      <p className="set-note">{he ? 'אימייל ריק שומר את הטלפון בלבד.' : 'A blank email saves only the phone.'}</p>
      <label className="set-field-label" htmlFor="contact-email">{he ? 'אימייל' : 'Email'}</label>
      <div className="set-row"><input id="contact-email" className="set-input" type="email" dir="ltr" autoComplete="email"
        maxLength={255} disabled={busy || !card} value={email} onChange={e => setEmail(e.target.value)}/></div>
      <label className="set-field-label" htmlFor="contact-phone">{he ? 'טלפון (רשות)' : 'Phone (optional)'}</label>
      <div className="set-row"><input id="contact-phone" className="set-input" type="tel" dir="ltr" autoComplete="tel"
        maxLength={32} disabled={busy || !card} value={phone} onChange={e => setPhone(e.target.value)}/></div>
      <div className="set-row"><button type="submit" className="btn btn-sm" disabled={busy || !card}>{he ? 'שמור' : 'Save'}</button>
        <button type="button" className="btn btn-sm" disabled={busy || !card?.email || email !== card.email}
          onClick={() => act('resend')}>{he ? 'קוד חדש' : 'Resend code'}</button></div>
    </form>
    {card?.email_pending && <form onSubmit={e => { e.preventDefault(); act('verify'); }}>
      <label className="set-field-label" htmlFor="contact-code">{he ? 'קוד אימות בן 8 תווים' : 'Eight-character verification code'}</label>
      <div className="set-row"><input id="contact-code" className="set-input mono" dir="ltr" type="text" autoComplete="one-time-code"
        required minLength={8} maxLength={8} pattern="[A-Za-z0-9]{8}" value={code} disabled={busy}
        onChange={e => setCode(e.target.value)}/>
        <button type="submit" className="btn btn-sm primary" disabled={busy}>{he ? 'אימות' : 'Verify'}</button></div>
    </form>}
    {message && <p className="set-status" role="status" dir="auto">{message}</p>}
    {error && <p className="set-error" role="alert" dir="auto">{error}</p>}
  </div>;
}

// The acting admin's own Telegram binding (spec 2026-09-17-agent-tasks §6).
// Its own card, not a column of either table below: it is about the PERSON
// looking at the page, which is the same reason ContactCard above is one.
// It reads GET /api/v1/auth/me/telegram -- {bound, pending, link_open},
// each account as {id, username, name}.
//
// A binding is TWO-WAY (review C1): the t.me link's /start only makes the
// sending account PENDING, and the bot sends THAT chat a six-digit code
// which is typed here. So the card shows WHICH account is pending beside
// the confirm box -- that display is the only defence against a bot-token
// holder who got its own account pending and sent its own code into the
// admin's chat. It shows it as three labelled slots
// (telegram-account.mjs, review NEW-1): the numeric id and the @username,
// which Telegram controls, then the first name quoted and labelled
// unverified, because an attacker's first name can read exactly like the
// admin's @username and id.
//
// Rendered only for a named admin (UsersSection's own namedAdmin, as
// ContactCard already is): every route here 403s the break-glass secret,
// which has no contact card.
function MyTelegramCard({ lang }) {
  const he = lang === 'he';
  const [state, setState] = useState(null);      // {bound, pending, link_open} | null
  const [link, setLink] = useState(null);        // {link, expires_in_s} | null
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = () => PCMAdapter.getMyTelegram().then((d) => { setState(d); return d; })
    .catch((e) => { setError(String((e && e.detail) || e.message || e)); return null; });
  const fail = (e) => { setError(String((e && e.detail) || e.message || e)); setBusy(false); };

  useEffect(() => { refresh(); }, []);

  // `bindLinkView`, not `link` alone: link_open is the server's answer over
  // the bind code's OWN expiry, so a link handed out ten minutes ago must
  // stop reading as live even though this component still holds it.
  const view = bindLinkView(state, link);

  // The /start arrives from the phone, not from this page: while the link
  // is still live and nothing is pending yet, look again every few seconds.
  // It stops by itself when an account turns up or the link expires.
  useEffect(() => {
    if (view !== 'live') return undefined;
    const timer = setInterval(refresh, 4000);
    return () => clearInterval(timer);
  }, [view]);

  const bind = () => {
    setError(''); setBusy(true);
    PCMAdapter.startMyTelegram()
      // refresh BEFORE clearing busy: link_open decides whether the link
      // renders, so setting the link against a stale state would hide it
      // for one paint.
      .then((d) => refresh().then((fresh) => {
        // The POST succeeded, so a link IS open. If the read-back itself
        // failed, say so rather than hiding the one thing this button was
        // pressed for -- a minted code with no link on screen is a dead end.
        if (!fresh) setState((prev) => ({ bound: (prev && prev.bound) || null, pending: null, link_open: true }));
        setLink(d); setCode(''); setBusy(false);
      }))
      .catch(fail);
  };

  const confirm = () => {
    setError(''); setBusy(true);
    PCMAdapter.confirmMyTelegram(code.trim())
      .then((d) => { setState(d); setLink(null); setCode(''); setBusy(false); })
      // A wrong code leaves the pending account in place (three tries); an
      // expired one clears it. Only the server knows which, so re-read.
      .catch((e) => { fail(e); refresh(); });
  };

  const unbind = async () => {
    if (!await confirmAction(he ? 'לנתק את חשבון הטלגרם?' : 'Unbind this Telegram account?', { lang, danger: true, confirmLabel: he ? 'ניתוק החשבון' : 'Unbind account' })) return;
    setError(''); setBusy(true);
    PCMAdapter.unbindMyTelegram()
      .then((d) => { setState(d); setLink(null); setCode(''); setBusy(false); })
      .catch(fail);
  };

  const slots = (a) => accountSlots(a, he).map((s) => (
    <p key={s.key} className="set-note">
      {s.label}: <bdi className="mono" dir={s.key === 'name' ? 'auto' : 'ltr'}>{s.value}</bdi>
    </p>
  ));
  const bound = state && state.bound;
  const pending = state && state.pending;

  return <div className="card set-section wide">
    <h3>{he ? 'הטלגרם שלי' : 'My Telegram'}</h3>
    <p className="set-note">{he
      ? 'הזמנות ואישורים בטלגרם עובדים רק לאחר קישור. כתיבות ל-ERP לעולם אינן מוכרעות שם.'
      : 'Orders and approvals by Telegram work only once bound. ERP writes are never decided there.'}</p>
    <p className="set-status" role="status">{state === null ? '…'
      : bound ? (he ? 'מקושר' : 'Bound') : pending ? (he ? 'ממתין לאישור' : 'Waiting for confirmation')
        : (he ? 'לא מקושר' : 'Not bound')}</p>
    {bound && <>
      <p className="set-note">{he ? 'מקושר לחשבון:' : 'Bound to this account:'}</p>
      {slots(bound)}
    </>}
    {pending && <>
      <p className="set-note">{he ? 'ממתין לאישור:' : 'Waiting for confirmation:'}</p>
      {slots(pending)}
      <p className="set-note">{he
        ? 'זה חשבון הטלגרם שלך? בדקו את המזהה ואת שם המשתמש (@) — השם הוא מה שהחשבון הקליד, ואינו מוכיח דבר. אם כן, הקלידו את הקוד בן שש הספרות שהבוט שלח אליו. אם לא — אל תאשרו, ובקשו קישור חדש.'
        : 'Is this your Telegram account? Check the id and the @username -- the name is whatever the account typed and proves nothing. If it is, type the six-digit code the bot sent it. If it is not, do not confirm: ask for a new link.'}</p>
      <form onSubmit={(e) => { e.preventDefault(); confirm(); }}>
        <label className="set-field-label" htmlFor="telegram-code">
          {he ? 'קוד בן שש ספרות' : 'Six-digit code'}
        </label>
        <div className="set-row">
          <input id="telegram-code" className="set-input mono" dir="ltr" type="text"
                 inputMode="numeric" autoComplete="one-time-code" required
                 minLength={6} maxLength={6} pattern="[0-9]{6}" disabled={busy}
                 value={code} onChange={(e) => setCode(e.target.value)}/>
          <button type="submit" className="btn btn-sm primary" disabled={busy || code.trim().length !== 6}>
            {he ? 'אישור' : 'Confirm'}
          </button>
        </div>
      </form>
    </>}
    <div className="set-row">
      {/* A new link clears a pending binding server-side: it is a new
          attempt (review NEW-6), so the account shown above changes. */}
      <button type="button" className="btn btn-sm" disabled={busy || state === null} onClick={bind}>
        {bound ? (he ? 'קשר חשבון אחר' : 'Bind another account') : (he ? 'קשר טלגרם' : 'Bind Telegram')}
      </button>
      {(bound || pending) && <button type="button" className="btn btn-sm danger" disabled={busy} onClick={unbind}>
        {he ? 'נתק' : 'Unbind'}
      </button>}
    </div>
    {view === 'live' && <p className="set-note">
      <a href={link.link} target="_blank" rel="noreferrer" dir="ltr">{link.link}</a>{' — '}
      {he ? `בתוקף ${Math.round(link.expires_in_s / 60)} דקות; פתחו בטלפון. החשבון יוצג כאן לאישור.`
        : `valid for ${Math.round(link.expires_in_s / 60)} minutes -- open it on your phone. The account shows here to confirm.`}
    </p>}
    {view === 'expired' && <p className="set-note">{he
      ? 'הקישור פג. לחצו "קשר טלגרם" לקישור חדש.'
      : 'That link has expired. Press Bind Telegram for a new one.'}</p>}
    {error && <p className="set-error" role="alert" dir="auto">{error}</p>}
  </div>;
}

const MCP_SCOPE_LABELS = {
  'data:read': ['Read data', 'קריאת נתונים'],
  'memory:write': ['Save and forget memories', 'שמירה ומחיקה של זיכרונות'],
  'business:write': ['Business actions', 'פעולות עסקיות'],
  'tasks:manage': ['Manage agent tasks', 'ניהול משימות סוכן'],
  'development:run': ['Priority development', 'פיתוח בפריוריטי'],
  'admin:read': ['Read administration', 'צפייה בניהול'],
  'admin:propose': ['Propose administrative changes', 'הצעת שינויים בניהול'],
};

function McpTokensCard({ lang }) {
  const he = lang === 'he';
  const id = React.useId();
  const [state, setState] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [auto, setAuto] = useState(false);
  const [minted, setMinted] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const fail = (e) => { setError(String((e && e.detail) || e.message || e)); setBusy(false); };
  const refresh = () => {
    setError('');
    return PCMAdapter.getMyMcpTokens().then((data) => {
      setState(data);
      setScopes(current => current === null ? (data.default_scopes || ['data:read'])
        : current.filter(scope => (data.allowed_scopes || []).includes(scope)));
    }).catch(fail);
  };
  useEffect(() => { refresh(); }, []);
  if (!state) return <div className="set-section">
    {error ? <><div className="set-error" role="alert">{error}</div>
      <button type="button" className="btn btn-sm" onClick={refresh}>{he ? 'נסה שוב' : 'Retry'}</button></>
      : <p className="set-status">{he ? 'טוען…' : 'Loading…'}</p>}
  </div>;
  if (!state.enabled) return <p className="set-note">{he ? 'גישת MCP כבויה בהתקנה הזו.' : 'MCP access is off on this installation.'}</p>;
  const selected = (scopes || []).filter(scope => state.allowed_scopes.includes(scope));
  const canAuto = selected.includes('development:run');
  const label = scope => MCP_SCOPE_LABELS[scope]?.[he ? 1 : 0] || scope;

  const mint = () => {
    setBusy(true); setError('');
    PCMAdapter.mintMyMcpToken({ name: name.trim(), days: Number(days), allow_auto: canAuto && auto, scopes: selected })
      .then((d) => { setMinted(d); setName(''); setAuto(false); setBusy(false); return refresh(); })
      .catch(fail);
  };
  const revoke = async (id, oauth, clientName) => {
    const question = oauth ? (he ? `לנתק את ${clientName || 'היישום'}?` : `Disconnect ${clientName || 'this app'}?`) : (he ? 'לבטל את הטוקן?' : 'Revoke this token?');
    const confirmLabel = oauth ? (he ? 'ניתוק' : 'Disconnect') : (he ? 'ביטול הטוקן' : 'Revoke token');
    if (!await confirmAction(question, { lang, danger: true, confirmLabel })) return;
    setBusy(true); setError('');
    PCMAdapter.revokeMyMcpToken(id).then(() => { setBusy(false); return refresh(); }).catch(fail);
  };
  // The token is shown once, alone; the command names $PM_TOKEN, so the token never
  // lands in shell history (spec T1, review M5).
  const cmd = `read -s PM_TOKEN && claude mcp add --transport http priority ${state.endpoint} --header "Authorization: Bearer $PM_TOKEN"`;

  return (
    <div className="card set-section wide">
      <h3>{he ? 'חיבורים וטוקנים ל-MCP' : 'MCP connections and tokens'}</h3>
      {error && <div className="set-error" role="alert">{error}</div>}
      <div className="tbl-wrap"><table className="tbl">
        <thead><tr>
          <th>{he ? 'שם' : 'Name'}</th><th>{he ? 'מזהה' : 'Prefix'}</th>
          <th>{he ? 'הרשאות' : 'Capabilities'}</th>
          <th>{he ? 'אוטומטי' : 'Auto'}</th><th>{he ? 'פג' : 'Expires'}</th>
          <th>{he ? 'שימוש אחרון' : 'Last used'}</th><th></th>
        </tr></thead>
        <tbody>
          {state.tokens.map((t) => (
            <tr key={t.id} className={t.live ? '' : 'dim'}>
              <td dir="auto">{t.kind === 'oauth' ? `${t.client_name || (he ? 'יישום' : 'App')} · ${he ? 'יישום מחובר' : 'Connected app'}` : t.name}</td>
              <td className="mono">{t.kind === 'oauth' ? '—' : `pmm_${t.display_prefix}…`}</td>
              <td>{t.scopes == null ? (he ? 'גישת הפיתוח המקורית' : 'Legacy development access') : t.scopes.map(label).join(', ')}</td>
              <td>{t.allow_auto ? '✓' : ''}</td>
              <td className="mono">{String(t.expires_at || '').slice(0, 10)}</td>
              <td className="mono">{t.last_used_at ? `${String(t.last_used_at).slice(0, 16)} ${t.last_used_ip || ''}` : '—'}</td>
              <td>{t.live && <button type="button" className="btn btn-sm danger" disabled={busy} onClick={() => revoke(t.id, t.kind === 'oauth', t.client_name)}>{t.kind === 'oauth' ? (he ? 'ניתוק' : 'Disconnect') : (he ? 'בטל' : 'Revoke')}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {state.tokens.some(t => t.kind === 'oauth') && <p className="set-note">{he
        ? 'ניתוק חוסם קריאות חדשות, רענון והצעות. עבודה שכבר פועלת ומשימות שנרשמו ממשיכות. קישורי הורדה קיימים לשימוש יחיד תקפים עד 10 דקות.'
        : 'Disconnect blocks new calls, refresh and proposals. Running work and committed jobs continue. Existing single-use download links remain valid for up to 10 minutes.'}</p>}
      <div className="set-row">
        <label className="set-field-label" htmlFor={`${id}-name`}>{he ? 'שם הטוקן' : 'Token name'}</label>
        <input id={`${id}-name`} className="set-input" value={name} maxLength={60}
               placeholder={he ? 'שם (למשל: מחשב נייד)' : 'Name (e.g. laptop)'}
               onChange={(e) => setName(e.target.value)} dir="auto"/>
        <label className="set-field-label" htmlFor={`${id}-days`}>{he ? 'תוקף' : 'Lifetime'}</label>
        <select id={`${id}-days`} value={days} onChange={(e) => setDays(e.target.value)}>
          {[7, 30, 90].map((d) => <option key={d} value={d}>{d} {he ? 'ימים' : 'days'}</option>)}
        </select>
      </div>
      <fieldset disabled={busy}>
        <legend>{he ? 'הרשאות הטוקן' : 'Token capabilities'}</legend>
        {state.allowed_scopes.map(scope => <label key={scope} className="set-radio" style={{ minHeight: 32 }} htmlFor={`${id}-${scope}`}>
          <input id={`${id}-${scope}`} type="checkbox" checked={selected.includes(scope)} onChange={e => {
            setScopes(e.target.checked ? [...selected, scope] : selected.filter(value => value !== scope));
            if (scope === 'development:run' && !e.target.checked) setAuto(false);
          }}/>{label(scope)}
        </label>)}
      </fieldset>
      <div className="set-row">
        {canAuto && <label htmlFor={`${id}-auto`}><input id={`${id}-auto`} type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)}/>
          {he ? ' מותר מצב אוטומטי' : ' Allow auto mode'}</label>}
        <button type="button" className="btn btn-sm" disabled={busy || !name.trim()} onClick={mint}>
          {he ? 'צור טוקן' : 'Mint token'}</button>
      </div>
      {minted && window.OnceBox && (
        <window.OnceBox label={he ? 'הטוקן (מוצג פעם אחת)' : 'The token (shown once)'}
                        value={minted.token} lang={lang}
                        note={he ? 'העתק עכשיו: הטוקן לא יוצג שוב.' : 'Copy it now: it is not shown again.'}/>
      )}
      <p className="set-note mono" dir="ltr">{cmd}</p>
      <p className="set-note">{he
        ? 'הפקודה מבקשת את הטוקן בלי להציג אותו. במחשב משותף בחר 7 ימים; ביטול הוא לחיצה אחת.'
        : 'The command asks for the token without echoing it. On a shared machine pick 7 days; revoking is one click.'}</p>
    </div>
  );
}

function McpTokensDialog({ lang, onClose }) {
  return <Modal label={lang === 'he' ? 'טוקנים ל-MCP' : 'MCP tokens'} onClose={onClose}>
    <McpTokensCard lang={lang}/>
    <div className="dlg-actions"><button type="button" className="btn btn-sm" onClick={onClose}>{lang === 'he' ? 'סגור' : 'Close'}</button></div>
  </Modal>;
}

function UsersSection({ lang }) {
  const [namedAdmin, setNamedAdmin] = useState(false);
  const [seatsOn, setSeatsOn] = useState(false);
  useEffect(() => { PCMAdapter.getMe().then(me => { setNamedAdmin(me.role === 'admin' && me.user_id != null); setSeatsOn(!!me.seats_enabled); }).catch(() => {}); }, []);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const load = () => PCMAdapter.getTenants().then(d => {
    setTenants(d.tenants);
    setTenantId(prev => prev ?? (d.tenants[0] ? d.tenants[0].id : null));
  }).catch(() => setTenants([]));
  useEffect(() => { load(); }, []);
  return (
    <>
      {namedAdmin && <ContactCard lang={lang}/>}
      {namedAdmin && <MyTelegramCard lang={lang}/>}
      {namedAdmin && <McpTokensCard lang={lang}/>}
      {tenantId !== null && <UsersCard key={tenantId} lang={lang} tenants={tenants} tenantId={tenantId} namedAdmin={namedAdmin} seatsOn={seatsOn} onTenant={(id) => { if (typeof id === 'number') setTenantId(id); else load(); }}/>}
      {seatsOn && tenants.length > 0 && <TenantLimitsCard lang={lang} tenants={tenants}/>}
      {seatsOn && tenants.length > 0 && <AgentLimitsCard lang={lang} tenants={tenants} company/>}
      <UsersCard lang={lang} tenants={tenants} tenantId={null}/>
    </>
  );
}

// A tenant login's own page (#team; agent seats spec §6-§7): its agent seat's
// contact and Telegram cards, and -- for a company admin -- its company's
// seats, users, tasks and grants. team-logic.mjs only decides which cards to
// render; the server allows exactly the same.
function TeamPage({ lang }) {
  const t = T[lang === 'he' ? 'he' : 'en'];
  const [me, setMe] = useState(undefined);
  useEffect(() => { PCMAdapter.getMe().then(setMe).catch(() => setMe(null)); }, []);
  if (me === undefined) return <div className="set-status mono">…</div>;
  const sections = teamSections(me);
  const has = (s) => sections.includes(s);
  return (
    <>
      <h1 className="page-title">{t.team}</h1>
      {has('contact') && <p className="set-note" dir="auto">{t.waitNote}</p>}
      <div className="set-page">
        {sections.length === 0 && <div className="card set-section wide"><p className="set-note" dir="auto">{t.noTeam}</p></div>}
        {has('contact') && <ContactCard lang={lang}/>}
        {has('telegram') && <MyTelegramCard lang={lang}/>}
        {has('company') && <TenantLimitsCard lang={lang} tenants={[]} tenantId={me.tenant_id}/>}
        {/* One card: its own half for the seat holder (the contact card's viewer), its
            company half for the company admin. */}
        {has('limits') && <AgentLimitsCard lang={lang} tenantId={me.tenant_id} own={has('contact')} company={has('company')} selfId={me.user_id}/>}
        {has('users') && <UsersCard lang={lang} tenants={[]} tenantId={me.tenant_id} own seatsOn={!!me.seats_enabled} selfId={me.user_id}/>}
        {has('tasks') && <CompanyTasksCard lang={lang} tenantId={me.tenant_id}/>}
        {has('permissions') && window.PermsCard && <window.PermsCard lang={lang} tenants={[]} tenantId={me.tenant_id} own/>}
      </div>
    </>
  );
}

function ChangePasswordDialog({ lang, onClose }) {
  const t = T[lang === 'he' ? 'he' : 'en'].pw;
  const problems = T[lang === 'he' ? 'he' : 'en'].problem;
  const [cur, setCur] = useState(''); const [next, setNext] = useState(''); const [again, setAgain] = useState('');
  const [error, setError] = useState(''); const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(null);
  const doneButton = React.useRef(null);
  useEffect(() => { if (done) doneButton.current?.focus(); }, [done]);
  const submit = () => {
    setError(''); setInvalid(null);
    if (next !== again) { setError(t.differ); setInvalid('again'); return; }
    const slug = PCMAdapter.getSlug() || null;
    PCMAdapter.changePassword({ slug: slug, username: PCMAdapter.getUsername(), current_password: cur, new_password: next })
      .then(() => setDone(true))
      // A 401 is the route's own "wrong current password" (never a dead session --
      // the adapter keeps it); say it in the page's language, like the login page does.
      .catch(e => { const d = e && e.detail; setInvalid(e?.status === 401 ? 'current' : d && typeof d === 'object' && d.code ? 'next' : null); setError(e && e.status === 401 ? t.invalid : d && typeof d === 'object' && d.code ? (problems[d.code] || d.code) : (typeof d === 'string' && d) || String(e.message || e)); });
  };
  return (
    <Modal label={t.title} onClose={onClose}>
        <h3>{t.title}</h3>
        {done ? <p className="set-note" role="status">{t.done}</p> : (
          <>
            <p className="set-note" id="password-rule">{t.rule}</p>
            <label className="set-field-label" htmlFor="password-current">{t.current}</label>
            <input className="set-input" type="password" autoComplete="current-password" id="password-current" aria-invalid={invalid === 'current' ? true : undefined} aria-describedby={error ? 'password-rule password-error' : 'password-rule'} value={cur} onChange={e => { setCur(e.target.value); setError(''); setInvalid(null); }}/>
            <label className="set-field-label" htmlFor="password-next">{t.next}</label>
            <input className="set-input" type="password" autoComplete="new-password" id="password-next" aria-invalid={invalid === 'next' ? true : undefined} aria-describedby={error ? 'password-rule password-error' : 'password-rule'} value={next} onChange={e => { setNext(e.target.value); setError(''); setInvalid(null); }}/>
            <label className="set-field-label" htmlFor="password-again">{t.again}</label>
            <input className="set-input" type="password" autoComplete="new-password" id="password-again" aria-invalid={invalid === 'again' ? true : undefined} aria-describedby={error ? 'password-rule password-error' : 'password-rule'} value={again} onChange={e => { setAgain(e.target.value); setError(''); setInvalid(null); }}
                   onKeyDown={e => { if (e.key === 'Enter') submit(); }}/>
            {error && <div id="password-error" className="set-error" role="alert" dir="auto">{error}</div>}
          </>
        )}
        <div className="dlg-actions">
          {!done && <button type="button" className="btn btn-sm primary" disabled={!cur || !next} onClick={submit}>{t.change}</button>}
          <button ref={doneButton} type="button" className="btn btn-sm" onClick={onClose}>{done ? 'OK' : t.cancel}</button>
        </div>
    </Modal>
  );
}

window.UsersSection = UsersSection;
window.OfflineDeveloperCreateCard = OfflineDeveloperCreateCard;
window.TeamPage = TeamPage;
window.ChangePasswordDialog = ChangePasswordDialog;
window.McpTokensDialog = McpTokensDialog;
window.McpScopeLabels = MCP_SCOPE_LABELS;
